/**
 * uw-pack-plan.js
 *
 * Multi-dog pack picker for products sold as N × one bottle (JOLLY GUT,
 * NO RUFF). Shoppers list up to 4 dogs (name, breed, weight) and pick one
 * supply tier for the order; each dog gets the bottles its weight needs for
 * that tier. Products register their data with UwPackPlan.define() from
 * their own file (jolly-gut-supply.js, no-ruff-supply.js).
 *
 * Cart: the theme's product-form posts `new FormData(form)` to /cart/add.
 * A `formdata` listener rewrites that payload: one `items[n]` line per dog
 * with properties[Dog] = "Bruno · Labrador · 20–40 kg", plus the
 * product's own pet list (attributes[pets_jolly_gut] / [pets_no_ruff]) and
 * attributes[pet_weight]. One unnamed dog keeps the
 * plain id + quantity payload. Shopify's "Buy N bottles" automatic
 * discounts count every line of the variant, so dogs combine for pricing.
 *
 * A/B test: the picker's data-pack-variant is "A" (classic: four weight
 * buttons, one pet at 10–20 kg) or "B" (multi-pet). snippets/uw-pack-ab.liquid
 * sets it inline before first paint; in the quick view, where inline
 * scripts don't run, the engine draws it from data-pack-share-b (empty or
 * invalid = 0, all A). With neither attribute the picker runs B (what the
 * tests mount).
 */
(function () {
  const MAX_DOGS = 4;
  const NAME_MAX = 30;
  const BREED_MAX = 40;
  const WEIGHT_RANGE = {
    small: "Up to 10 kg",
    medium: "10–20 kg",
    large: "20–40 kg",
    xlarge: "Over 40 kg",
  };
  // attributes[pet_weight] keeps its existing values; downstream reads them.
  const WEIGHT_ATTR = {
    small: "up to 10 kg",
    medium: "10-20 kg",
    large: "20-40 kg",
    xlarge: "over 40 kg",
  };

  const weightRange = (weight) => WEIGHT_RANGE[weight] || WEIGHT_RANGE.medium;
  const weightAttr = (weight) => WEIGHT_ATTR[weight] || WEIGHT_ATTR.medium;

  function formatMoney(rupees) {
    return "₹" + Math.round(rupees).toLocaleString("en-IN");
  }

  // Total pack size for the sticky bar, short enough for mobile:
  // 2 × "200 g" → "400 g", 7 × "200 g" → "1.4 kg", 5 × "200 ml" → "1 L".
  function formatTotalAmount(qty, unitTitle) {
    const match = /^([\d.]+)\s*(g|ml)\b/i.exec(unitTitle || "");
    if (!match) return `${qty} × ${unitTitle}`;
    const total = qty * parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    if (total >= 1000) {
      return `${Number((total / 1000).toFixed(1))} ${unit === "g" ? "kg" : "L"}`;
    }
    return `${total} ${unit}`;
  }

  // ";" separates dogs in the pets_* attribute and "·" separates fields. The
  // text also becomes a line-item property shown in cart, order and emails,
  // so HTML-significant characters go too.
  function cleanText(value, max) {
    return String(value || "")
      .replace(/[;·\r\n<>"&]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, max)
      .trim();
  }

  function priceForQty(config, qty) {
    if (qty <= 0) return 0;
    const ladder = config.PRICE_BY_QTY[qty];
    if (ladder != null) return ladder;
    // Shopify applies the best qualifying automatic discount, so 8+ bottles
    // get the top tier's percentage.
    return Math.floor(
      qty * config.BASE_PRICE_PER_BOTTLE * (1 - config.TOP_TIER_DISCOUNT),
    );
  }

  function qualifiesForFreeShipping(config, price) {
    return (
      config.FREE_SHIPPING_THRESHOLD != null &&
      price >= config.FREE_SHIPPING_THRESHOLD
    );
  }

  // Unknown weights (a stale or tampered <select>) count as medium.
  function knownWeight(config, weight) {
    return config.SUPPLY_BY_WEIGHT_AND_TILE[weight] ? weight : "medium";
  }

  function planFor(config, dogs, tile) {
    const table = config.SUPPLY_BY_WEIGHT_AND_TILE;
    const lines = dogs.map((dog) => {
      const cells = table[knownWeight(config, dog.weight)];
      const index = Math.max(0, Math.min(Number(tile) || 0, cells.length - 1));
      const cell = cells[index];
      return { dog, qty: cell.qty, days: cell.days, share: 0 };
    });
    const totalQty = lines.reduce((n, line) => n + line.qty, 0);
    const price = priceForQty(config, totalQty);
    // Each dog's share at the combined rate; the last absorbs rounding so
    // the shares always add up to the order price.
    let allocated = 0;
    lines.forEach((line, i) => {
      line.share =
        i === lines.length - 1
          ? price - allocated
          : Math.round((price * line.qty) / totalQty);
      allocated += line.share;
    });
    const mrp = totalQty * config.MRP_PER_BOTTLE;
    return { lines, totalQty, price, mrp, save: Math.max(0, mrp - price) };
  }

  function dogName(config, dog, index) {
    const noun = config.noun === "pet" ? "Pet" : "Dog";
    return cleanText(dog.name, NAME_MAX) || `${noun} ${index + 1}`;
  }

  function dogLabel(config, dog, index) {
    return [
      dogName(config, dog, index),
      cleanText(dog.breed, BREED_MAX),
      weightRange(dog.weight),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  // Separate cart lines are only needed when there is something to tell apart.
  function usesItemsMode(dogs) {
    return (
      dogs.length > 1 ||
      dogs.some(
        (d) => cleanText(d.name, NAME_MAX) || cleanText(d.breed, BREED_MAX),
      )
    );
  }

  // One cart attribute per product: a shared key let a later single-pet add
  // of the other product wipe this product's pet list.
  function petsAttr(config) {
    return `attributes[pets_${String(config.key).replace(/-/g, "_")}]`;
  }

  // Same rules as the inline copy in snippets/uw-pack-ab.liquid (kept in
  // step by tests/uw-pack-ab.test.js). The cookie holds a 0–99 bucket drawn
  // once, so raising the share only moves A visitors to B.
  function abVariant(shareB) {
    const share = Math.max(0, Math.min(100, Number(shareB) || 0));
    const match = /(?:^|;\s*)uw_pack_ab=(\d|[1-9]\d)(?:;|$)/.exec(document.cookie);
    let bucket;
    if (match) {
      bucket = Number(match[1]);
    } else {
      bucket = Math.floor(Math.random() * 100);
      document.cookie = `uw_pack_ab=${bucket}; max-age=7776000; path=/; SameSite=Lax`;
    }
    return bucket < share ? "B" : "A";
  }

  function applyCartPayload(
    config,
    formData,
    { plan, variantId, weightTouched, variant },
  ) {
    const dogs = plan.lines.map((line) => line.dog);
    // Orders carry the test group, for the read-out by order.
    if (variant) formData.set("attributes[ab_pack_plan]", variant);
    if (weightTouched) {
      formData.set(
        "attributes[pet_weight]",
        dogs.map((d) => weightAttr(d.weight)).join(", "),
      );
    } else if (variant === "A") {
      // As the old picker's always-sent hidden input did: clear a weight
      // left over from an earlier add.
      formData.set("attributes[pet_weight]", "");
    }
    if (!usesItemsMode(dogs)) {
      formData.set("quantity", String(plan.totalQty));
      // Shopify removes a cart attribute set to "", so this clears any
      // pet list left over from an earlier multi-pet add of this product.
      formData.set(petsAttr(config), "");
      return formData;
    }
    const property = config.propertyName || "Dog";
    formData.set(
      petsAttr(config),
      plan.lines.map((line, i) => dogLabel(config, line.dog, i)).join("; "),
    );
    // Verified on the live store: sending `id` alongside items[] makes
    // Shopify add an extra 1-bottle line, so `id` must go. If /cart/add then
    // rejects the add, the theme's error path (which reads `id`) shows its
    // generic "select a variant" message. Accepted: it's rare (stock is
    // continue-selling) and nothing is half-added.
    formData.delete("id");
    formData.delete("quantity");
    plan.lines.forEach((line, i) => {
      formData.append(`items[${i}][id]`, String(variantId));
      formData.append(`items[${i}][quantity]`, String(line.qty));
      formData.append(
        `items[${i}][properties][${property}]`,
        dogLabel(config, line.dog, i),
      );
    });
    return formData;
  }

  function define(tagName, config) {
    if (customElements.get(tagName)) return;
    const key = config.key;
    const sel = (name) => `[data-${key}-${name}]`;

    class PackPlan extends HTMLElement {
      constructor() {
        super();
        this.dogs = [{ name: "", breed: "", weight: "" }];
        this.selectedTile = 0; // Starter
        this._weightTouched = false;
        this._abort = null;
        this._stickySelect = null;
      }

      connectedCallback() {
        this._abort = new AbortController();
        this._formId = this.getAttribute(`data-${key}-product-form-id`);
        if (!this._variant) this._initVariant();
        this._renderDogs();
        this._bindDogs();
        this._bindClassicWeights();
        this._bindSupplyTiles();
        this._bindForms();
        this._sync();
        // Once per element: the theme can move it, which reconnects.
        if (!this._viewTracked) {
          this._viewTracked = true;
          this._track("uw_pack_view", {
            uw_pack_context: this._quickview() ? "quickview" : "pdp",
          });
        }
      }

      disconnectedCallback() {
        if (this._abort) this._abort.abort();
        this._abort = null;
        // The abort removed the sticky select's listener; forget it so a
        // reconnect binds it again.
        this._stickySelect = null;
      }

      _initVariant() {
        let variant = this.dataset.packVariant;
        if (variant !== "A" && variant !== "B") {
          const share = this.dataset.packShareB;
          // Present but empty or invalid counts as 0: rollout is explicit.
          if (share != null) {
            if (!window.UwPackAb) window.UwPackAb = { variant: abVariant };
            variant = window.UwPackAb.variant(share);
          } else {
            variant = "B";
          }
        }
        this._variant = variant;
        // The CSS shows A until this is set, so set it even for the default.
        this.dataset.packVariant = variant;
        if (variant === "A") {
          // Classic control: one pet, weight preselected, never blocked.
          this.dogs = [{ name: "", breed: "", weight: "medium" }];
        }
        if (typeof window.clarity === "function") {
          window.clarity("set", "pack_plan_variant", variant);
        }
      }

      // Picker events for GTM → GA4 (plan §3). GTM loads deferred; pushes
      // queue in dataLayer until then. Counts and choices only: no pet names
      // or breed text.
      _track(name, params = {}) {
        (window.dataLayer = window.dataLayer || []).push({
          event: name,
          uw_pack_variant: this._variant,
          uw_pack_product: key,
          ...params,
        });
      }

      _selectTile(idx) {
        if (Number.isNaN(idx) || idx === this.selectedTile) return;
        this.selectedTile = idx;
        this._sync();
        this._track("uw_pack_tier_selected", { uw_pack_tier: idx });
      }

      _isClassic() {
        return this._variant === "A";
      }

      _signal() {
        return this._abort ? this._abort.signal : undefined;
      }

      _quickview() {
        return this.closest(".m-product-quickview");
      }

      plan() {
        return planFor(config, this._planDogs(), this.selectedTile);
      }

      // A is one anonymous pet: the hidden B fields can't add lines or names.
      _planDogs() {
        if (!this._isClassic()) return this.dogs;
        return [{ name: "", breed: "", weight: this.dogs[0].weight }];
      }

      _disableThemeQuantityInputs() {
        // The theme's buy-button block may emit its own <input name="quantity">.
        // Disable any that aren't ours so the form only submits our total.
        if (!this._formId) return;
        const ours = `[data-${key}-cart-qty]`;
        document
          .querySelectorAll(
            `form#${this._formId} input[name="quantity"]:not(${ours}), input[form="${this._formId}"][name="quantity"]:not(${ours})`,
          )
          .forEach((input) => {
            input.disabled = true;
          });
      }

      // ---- Dogs -----------------------------------------------------------

      _renderDogs() {
        const list = this.querySelector("[data-pack-dogs]");
        const template = this.querySelector("template[data-pack-dog-template]");
        if (!list || !template) return;
        const noun = config.noun === "pet" ? "Pet" : "Dog";
        list.replaceChildren(
          ...this.dogs.map((dog, i) => {
            const card = template.content.firstElementChild.cloneNode(true);
            card.dataset.dogIndex = String(i);
            const n = i + 1;
            const name = card.querySelector("[data-dog-name]");
            name.value = dog.name;
            name.setAttribute("aria-label", `${noun} ${n}'s name (optional)`);
            const breed = card.querySelector("[data-dog-breed]");
            breed.value = dog.breed;
            breed.setAttribute("aria-label", `${noun} ${n}'s breed (optional)`);
            const weight = card.querySelector("[data-dog-weight]");
            weight.value = dog.weight;
            weight.setAttribute("aria-label", `${noun} ${n}'s weight`);
            const remove = card.querySelector("[data-dog-remove]");
            if (remove) {
              remove.setAttribute(
                "aria-label",
                `Remove ${noun.toLowerCase()} ${n}`,
              );
            }
            return card;
          }),
        );
      }

      _dogFor(target) {
        const card = target.closest("[data-pack-dog]");
        return card ? this.dogs[Number(card.dataset.dogIndex)] : null;
      }

      _bindDogs() {
        const signal = this._signal();
        const list = this.querySelector("[data-pack-dogs]");
        if (list) {
          // On the product page we sit inside the theme's <variant-picker>,
          // which re-renders the headline price (single-bottle) on any
          // bubbling input/change, overwriting our plan total. Dog fields
          // aren't variant options, so keep their events to ourselves.
          const handleThenContain = (handler) => (e) => {
            handler(e);
            e.stopPropagation();
          };
          list.addEventListener(
            "input",
            handleThenContain((e) => {
              const dog = this._dogFor(e.target);
              if (!dog) return;
              if (e.target.matches("[data-dog-name]"))
                dog.name = e.target.value;
              else if (e.target.matches("[data-dog-breed]")) {
                dog.breed = e.target.value;
                this._showBreeds(e.target);
              } else return;
              // Names appear in the dose line; no card re-render, so focus stays.
              this._sync();
            }),
            { signal },
          );
          list.addEventListener(
            "change",
            handleThenContain((e) => {
              if (!e.target.matches("[data-dog-weight]")) return;
              const dog = this._dogFor(e.target);
              if (!dog) return;
              const changed = dog.weight !== e.target.value;
              dog.weight = e.target.value;
              this._weightTouched = true;
              this._flagMissingWeight(e.target.closest("[data-pack-dog]"), false);
              this._sync();
              if (changed) {
                this._track("uw_pack_weight_selected", {
                  uw_pack_weight: dog.weight,
                });
              }
            }),
            { signal },
          );
          list.addEventListener(
            "click",
            (e) => {
              const button = e.target.closest("[data-dog-remove]");
              if (!button || this.dogs.length < 2) return;
              const card = button.closest("[data-pack-dog]");
              const index = Number(card.dataset.dogIndex);
              this.dogs.splice(index, 1);
              this._renderDogs();
              this._sync();
              this._track("uw_pack_pet_removed", {
                uw_pack_pets: this.dogs.length,
              });
              // The clicked button is gone; keep keyboard users in place.
              const previous =
                this.querySelectorAll("[data-pack-dog]")[index - 1];
              const target =
                (previous && previous.querySelector("[data-dog-name]")) ||
                this.querySelector("[data-pack-add-dog]");
              if (target) target.focus();
            },
            { signal },
          );
          this._bindBreedSuggestions(list, signal);
        }
        const add = this.querySelector("[data-pack-add-dog]");
        if (add) {
          add.addEventListener(
            "click",
            () => {
              if (this._isClassic() || this.dogs.length >= MAX_DOGS) return;
              this.dogs.push({ name: "", breed: "", weight: "" });
              this._renderDogs();
              this._sync();
              this._track("uw_pack_pet_added", {
                uw_pack_pets: this.dogs.length,
              });
              const cards = this.querySelectorAll("[data-pack-dog]");
              const card = cards[cards.length - 1];
              // On mobile the new card can land below the fold.
              if (typeof card.scrollIntoView === "function") {
                card.scrollIntoView({ block: "nearest" });
              }
              const name = card.querySelector("[data-dog-name]");
              if (name) name.focus({ preventScroll: true });
            },
            { signal },
          );
        }
      }

      _bindClassicWeights() {
        if (!this._isClassic()) return;
        this.querySelectorAll("[data-pack-classic-weight]").forEach((button) => {
          button.addEventListener(
            "click",
            () => {
              const weight = button.dataset.packClassicWeight;
              // A click on the preselected weight still confirms it for
              // pet_weight, but isn't a new choice.
              const changed = this.dogs[0].weight !== weight;
              this.dogs[0].weight = weight;
              this._weightTouched = true;
              this._sync();
              if (changed) {
                this._track("uw_pack_weight_selected", {
                  uw_pack_weight: weight,
                });
              }
            },
            { signal: this._signal() },
          );
        });
      }

      _renderClassicWeights() {
        if (!this._isClassic()) return;
        const weight = this.dogs[0].weight;
        this.querySelectorAll("[data-pack-classic-weight]").forEach((button) => {
          const active = button.dataset.packClassicWeight === weight;
          button.classList.toggle("is-selected", active);
          button.setAttribute("aria-checked", active ? "true" : "false");
        });
      }

      // ---- Breed suggestions ---------------------------------------------
      // Our own list instead of the browser's datalist popup, which doesn't
      // fill the box reliably on phones. Free text is still allowed.

      _breedOptions() {
        if (!this._breeds) {
          this._breeds = Array.from(
            this.querySelectorAll("[data-pack-breeds] option"),
          ).map((option) => option.value);
        }
        return this._breeds;
      }

      _bindBreedSuggestions(list, signal) {
        list.addEventListener(
          "focusin",
          (e) => {
            if (e.target.matches("[data-dog-breed]")) this._showBreeds(e.target);
          },
          { signal },
        );
        list.addEventListener(
          "focusout",
          (e) => {
            if (!e.target.matches("[data-dog-breed]")) return;
            // Let a tap on a suggestion land before the list goes (and
            // before deciding the breed was typed rather than chosen).
            setTimeout(() => {
              // Gone with a removed pet: its card index now points elsewhere.
              if (!e.target.isConnected) return;
              if (document.activeElement === e.target) return;
              this._hideBreeds(e.target);
              this._reportBreed(e.target, "typed");
            }, 150);
          },
          { signal },
        );
        list.addEventListener(
          "keydown",
          (e) => {
            if (!e.target.matches("[data-dog-breed]")) return;
            const box = this._breedList(e.target);
            const options = box && !box.hidden
              ? Array.from(box.querySelectorAll("[data-breed-option]"))
              : [];
            if (e.key === "Escape") {
              this._hideBreeds(e.target);
              return;
            }
            if (!options.length) return;
            const current = options.findIndex((o) => o.classList.contains("is-active"));
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              const step = e.key === "ArrowDown" ? 1 : -1;
              const next = (current + step + options.length) % options.length;
              options.forEach((o, i) => o.classList.toggle("is-active", i === next));
              options[next].scrollIntoView?.({ block: "nearest" });
            } else if (e.key === "Enter" && current !== -1) {
              e.preventDefault();
              this._chooseBreed(e.target, options[current].textContent);
            }
          },
          { signal },
        );
        // Keep focus in the box while tapping a suggestion.
        list.addEventListener(
          "mousedown",
          (e) => {
            if (e.target.closest("[data-breed-option]")) e.preventDefault();
          },
          { signal },
        );
        list.addEventListener(
          "click",
          (e) => {
            const option = e.target.closest("[data-breed-option]");
            if (!option) return;
            const input = option
              .closest(".uw-dog__breed")
              ?.querySelector("[data-dog-breed]");
            if (input) this._chooseBreed(input, option.textContent);
          },
          { signal },
        );
      }

      _breedList(input) {
        return input.parentElement.querySelector("[data-breed-list]");
      }

      _showBreeds(input) {
        const query = input.value.trim().toLowerCase();
        const matches = this._breedOptions().filter((breed) =>
          breed.toLowerCase().includes(query),
        );
        let box = this._breedList(input);
        if (!matches.length) {
          this._hideBreeds(input);
          return;
        }
        if (!box) {
          box = document.createElement("ul");
          box.className = "uw-dog__suggest";
          box.setAttribute("role", "listbox");
          box.dataset.breedList = "";
          input.parentElement.appendChild(box);
        }
        box.replaceChildren(
          ...matches.map((breed) => {
            const item = document.createElement("li");
            item.setAttribute("role", "presentation");
            const button = document.createElement("button");
            button.type = "button";
            button.tabIndex = -1;
            button.setAttribute("role", "option");
            button.dataset.breedOption = "";
            button.textContent = breed;
            item.appendChild(button);
            return item;
          }),
        );
        box.hidden = false;
        input.setAttribute("aria-expanded", "true");
      }

      _hideBreeds(input) {
        const box = this._breedList(input);
        if (box) box.hidden = true;
        input.setAttribute("aria-expanded", "false");
      }

      _chooseBreed(input, breed) {
        input.value = breed;
        // Same path as typing: updates the pet's details.
        input.dispatchEvent(new Event("input", { bubbles: true }));
        this._hideBreeds(input);
        this._reportBreed(input, "list");
      }

      // One event per new breed value: the last reported value is kept per
      // pet (cards re-render, pets persist), so a later blur on a breed
      // just chosen from the list isn't counted again as typed.
      _reportBreed(input, source) {
        const dog = this._dogFor(input);
        const value = input.value.trim();
        if (!dog || !value) return;
        this._breedReported = this._breedReported || new WeakMap();
        if (this._breedReported.get(dog) === value) return;
        this._breedReported.set(dog, value);
        this._track("uw_pack_breed_selected", { uw_pack_breed_source: source });
      }

      _bindSupplyTiles() {
        this.querySelectorAll(sel("supply-tile")).forEach((button) => {
          button.addEventListener(
            "click",
            () => {
              this._selectTile(
                parseInt(button.getAttribute(`data-${key}-supply-tile`), 10),
              );
            },
            { signal: this._signal() },
          );
        });
      }

      // ---- Render ---------------------------------------------------------

      _sync() {
        // The theme can re-render the buy-buttons block, bringing back its
        // own quantity input.
        this._disableThemeQuantityInputs();
        const plan = this.plan();
        this._renderClassicWeights();
        this._renderDogCards(plan);
        this._renderTiles();
        const qty = this.querySelector(sel("cart-qty"));
        if (qty) qty.value = String(plan.totalQty);
        this._renderInfoCard(plan);
        this._updateThemePrice(plan);
        this._syncStickyBar(plan);
      }

      _renderDogCards(plan) {
        this.querySelectorAll("[data-pack-dog]").forEach((card, i) => {
          const line = plan.lines[i];
          if (!line) return;
          const meta = card.querySelector("[data-dog-meta]");
          if (meta) {
            meta.textContent = line.dog.weight
              ? `${line.qty} bottle${line.qty === 1 ? "" : "s"} · ${line.days} days`
              : "";
          }
          const remove = card.querySelector("[data-dog-remove]");
          if (remove) remove.hidden = i === 0;
        });
        const add = this.querySelector("[data-pack-add-dog]");
        if (add) add.hidden = this.dogs.length >= MAX_DOGS;
      }

      _renderTiles() {
        this.querySelectorAll(sel("supply-tile")).forEach((button) => {
          const idx = parseInt(
            button.getAttribute(`data-${key}-supply-tile`),
            10,
          );
          const active = idx === this.selectedTile;
          button.classList.toggle("is-selected", active);
          button.setAttribute("aria-checked", active ? "true" : "false");
          const tilePlan = planFor(config, this._planDogs(), idx);
          const days = button.querySelector(sel("supply-days"));
          if (days) {
            days.textContent =
              tilePlan.lines.length === 1
                ? `${tilePlan.lines[0].days} day pack`
                : `${tilePlan.totalQty} bottles`;
          }
          const ship = button.querySelector(sel("ship-flag"));
          if (ship)
            ship.hidden = !qualifiesForFreeShipping(config, tilePlan.price);
        });
      }

      _renderInfoCard(plan) {
        const card = this.querySelector(sel("info-card"));
        if (!card) return;
        const set = (name, text) => {
          const el = card.querySelector(`[data-${key}-info="${name}"]`);
          if (el) el.textContent = text;
        };
        set(
          "supply",
          `Includes: ${plan.totalQty} × ${config.unit} bottle${plan.totalQty === 1 ? "" : "s"}`,
        );
        set("price", formatMoney(plan.price));
        set("compare", plan.save > 0 ? formatMoney(plan.mrp) : "");
        set("save", plan.save > 0 ? `Save ${formatMoney(plan.save)}` : "");
        if (plan.lines.some((line) => !line.dog.weight)) {
          // Prices meanwhile assume 10–20 kg; dose and duration would be wrong.
          const prompt = `Select your ${config.noun === "pet" ? "pet" : "dog"}'s weight`;
          set("dose", prompt);
          set("duration", prompt);
        } else if (plan.lines.length === 1) {
          const weight = plan.lines[0].dog.weight;
          set(
            "dose",
            `${config.dose(knownWeight(config, weight))} daily · for pets ${weightAttr(weight)}`,
          );
        } else {
          set(
            "dose",
            plan.lines
              .map(
                (line, i) =>
                  `${dogName(config, line.dog, i)}: ${config.dose(knownWeight(config, line.dog.weight))}`,
              )
              .join(" · ") + " daily",
          );
        }
        if (plan.lines.every((line) => line.dog.weight)) {
          const days = plan.lines.map((line) => line.days);
          const min = Math.min(...days);
          const max = Math.max(...days);
          set("duration", min === max ? `${min} days` : `${min}–${max} days`);
        }
        const ship = card.querySelector(sel("info-ship"));
        if (ship) ship.hidden = !qualifiesForFreeShipping(config, plan.price);
      }

      _updateThemePrice(plan) {
        // Rewrite the theme's headline price to the order total. Only the
        // price blocks in our own context: the quick view's when we're in one,
        // otherwise the product page's (not a quick view open on top of it).
        const quickview = this._quickview();
        const blocks = Array.from(
          (quickview || document).querySelectorAll(
            ".main-product__block-price, .product-block-group--price",
          ),
        ).filter(
          (block) => block.closest(".m-product-quickview") === quickview,
        );
        blocks.forEach((block) => {
          block.querySelectorAll(".m-price-item--last").forEach((el) => {
            el.textContent = formatMoney(plan.price);
          });
          block.querySelectorAll("s.m-price-item--regular").forEach((el) => {
            el.textContent = formatMoney(plan.mrp);
          });
          block.querySelectorAll(".m-currency--saved").forEach((el) => {
            el.textContent = formatMoney(plan.save);
          });
        });
      }

      _syncStickyBar(plan) {
        // The theme's sticky add-to-cart bar is a separate form (variant
        // <select> + its own quantity) that only follows VARIANT_CHANGE. Packs
        // change quantity, not variant, so mirror the plan into the bar.
        // In the quick view, the page's sticky bar belongs to another product.
        if (this._quickview()) return;
        const sticky = document.querySelector("form.sticky-atc-form");
        const variantId = this._variantId();
        if (!sticky || !variantId) return;

        const select = sticky.querySelector('select[name="id"]');
        if (select) {
          // List the three tiers for these dogs; all the same variant, only the
          // quantity differs. Other variants are dropped: pack pricing only
          // covers this one.
          select.dataset.unitTitle = config.unit;
          const options = config.SUPPLY_BY_WEIGHT_AND_TILE.small.map(
            (_, idx) => {
              const tilePlan = planFor(config, this._planDogs(), idx);
              const opt = new Option(
                `${formatTotalAmount(tilePlan.totalQty, config.unit)} · ${formatMoney(tilePlan.price)}`,
                variantId,
              );
              opt.dataset.packTile = String(idx);
              return opt;
            },
          );
          select.replaceChildren(...options);
          select.selectedIndex = this.selectedTile;

          if (this._stickySelect !== select) {
            this._stickySelect = select;
            select.addEventListener(
              "change",
              () => {
                const opt = select.selectedOptions[0];
                this._selectTile(opt ? parseInt(opt.dataset.packTile, 10) : NaN);
              },
              { signal: this._signal() },
            );
            // The theme sets `select.value = variant.id` on VARIANT_CHANGE,
            // which jumps to the first tier. Put ours back after it.
            const productId = sticky.closest("sticky-atc")?.dataset.productId;
            if (productId && window.MinimogEvents) {
              const off = window.MinimogEvents.subscribe(
                `${productId}__VARIANT_CHANGE`,
                () => {
                  setTimeout(() => {
                    if (this._stickySelect === select) {
                      select.selectedIndex = this.selectedTile;
                    }
                  });
                },
              );
              // A reconnect subscribes again; drop this one with the rest.
              if (typeof off === "function") {
                this._signal()?.addEventListener("abort", off, { once: true });
              }
            }
          }
        }

        sticky.querySelectorAll('input[name="quantity"]').forEach((input) => {
          input.value = String(plan.totalQty);
        });
      }

      _bindForms() {
        // The theme's product-form posts `new FormData(form)`; rewrite it per
        // dog. The sticky bar has its own form, but only on our product page.
        const forms = [this._formId && document.getElementById(this._formId)];
        if (!this._quickview()) {
          forms.push(document.querySelector("form.sticky-atc-form"));
        }
        const ours = forms.filter(Boolean);
        ours.forEach((form) => {
          form.addEventListener(
            "formdata",
            (e) => this.applyCartPayload(e.formData),
            { signal: this._signal() },
          );
        });
        // Every pet needs a weight before adding: it sets the bottle count.
        // Capture on document runs before the theme's own submit handler.
        document.addEventListener(
          "submit",
          (e) => {
            if (!ours.includes(e.target)) return;
            const missing = this._isClassic()
              ? -1
              : this.dogs.findIndex((dog) => !dog.weight);
            if (missing === -1) {
              this._trackAdd(e.target);
              return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            this._track("uw_pack_add_blocked", {
              uw_pack_pets: this.dogs.length,
            });
            const card = this.querySelectorAll("[data-pack-dog]")[missing];
            this._flagMissingWeight(card, true);
            const select = card && card.querySelector("[data-dog-weight]");
            if (select) {
              if (typeof select.scrollIntoView === "function") {
                select.scrollIntoView({ block: "center" });
              }
              select.focus({ preventScroll: true });
            }
          },
          { capture: true, signal: this._signal() },
        );
      }

      _trackAdd(form) {
        const plan = this.plan();
        let source = "main";
        if (form.matches(".sticky-atc-form")) source = "sticky";
        else if (this._quickview()) source = "quickview";
        // Counts add attempts that passed the weight guard: it fires before
        // the theme's /cart/add request, so failed adds are included.
        this._track("uw_pack_add", {
          uw_pack_pets: plan.lines.length,
          uw_pack_bottles: plan.totalQty,
          uw_pack_named: plan.lines.some((line) =>
            cleanText(line.dog.name, NAME_MAX),
          ),
          uw_pack_source: source,
        });
      }

      _flagMissingWeight(card, missing) {
        if (!card) return;
        const select = card.querySelector("[data-dog-weight]");
        if (select) {
          if (missing) select.setAttribute("aria-invalid", "true");
          else select.removeAttribute("aria-invalid");
        }
        const error = card.querySelector("[data-dog-error]");
        if (error) error.hidden = !missing;
      }

      // Reads the input directly on purpose: any `new FormData()` built from
      // these forms is rewritten by our own `formdata` listener, which
      // deletes `id` in multi-dog mode.
      _variantId() {
        const form = this._formId && document.getElementById(this._formId);
        const input = form && form.querySelector('[name="id"]');
        return input ? input.value : null;
      }

      applyCartPayload(formData) {
        return applyCartPayload(config, formData, {
          plan: this.plan(),
          variantId: this._variantId() || formData.get("id"),
          weightTouched: this._weightTouched,
          variant: this._variant,
        });
      }
    }

    customElements.define(tagName, PackPlan);
  }

  const api = {
    MAX_DOGS,
    WEIGHT_ATTR,
    formatMoney,
    formatTotalAmount,
    cleanText,
    priceForQty,
    qualifiesForFreeShipping,
    planFor,
    dogName,
    dogLabel,
    usesItemsMode,
    applyCartPayload,
    abVariant,
    define,
  };

  window.UwPackPlan = window.UwPackPlan || api;
  if (typeof module !== "undefined" && module.exports) module.exports = api;
})();
